import { useFlyOutProductsBasedOnSelectedCloud } from '../../common/utils/hooks/useFlyOutProductsBasedOnSelectedCloud'
import {
  ModalConfirm,
  ModalSizeVariants,
  PlanType,
  TableComponent,
} from '@labrav/react-components'
import React, { useCallback } from 'react'
import { SelectChangeEvent } from '@mui/material'
import { useParams } from 'react-router-dom'
import {
  PartnerType,
  useAccountId,
  useCurrentCloudProvider,
  useUserType,
} from '@labrav/common'

import {
  getPrivateOffers,
  setPageNumber,
  updatePageSize,
} from '../../modules/privateOffer/action'
import { PrivateOffersState } from '../../modules/privateOffer/reducer'
import { useDispatch, useSelector } from '../../store'
import { CloudMarketplace } from '../../modules/productsListing/types'
import { getKeyCloudAccount } from '../../utils/privateOffersListHelpers'
import type {
  DetailedOfferPreviewComponent,
  DetailedOfferPreviewData,
} from './types'

export const mappingOnAdminUI = {
  'Offer name': 'privateOfferName',
  'Company name': 'companyName',
  'Product name': 'productName',
  'Offer object ID in CRM': 'crmOrderObjectId',
  'Created at': 'createdAt',
  'AWS offer ID': 'awsOfferId',
  'AWS offer URL': 'cloudOfferUrl',
  Actions: 'actions',
}

export const getMappingPOBasedOnCloud = (cloud: CloudMarketplace) => {
  const mappingValue = getKeyCloudAccount(cloud)
  return {
    [`${cloud} OFFER ID`]: mappingValue,
    'PRODUCT NAME': 'productName',
    'COMPANY NAME': 'companyName',
    STATUS: 'offerStatus',
    ...(cloud === 'AWS' ? { TYPE: 'type' } : {}),
    'CREATED AT': 'createdAt',
    'EXPIRY DATE': 'offerExpirationDate',
    ACTIONS: 'actions',
  }
}

export type PrivateOffersProps = {
  privateOffersForTable: Record<string, string | JSX.Element | null>[]
  openModal: boolean
  selectedPrivateOffer: DetailedOfferPreviewData
  handleModalClose: () => void
  crmOpportunityId?: string
  planType?: PlanType
  DetailedOfferPreviewComponent?: DetailedOfferPreviewComponent
}

const PrivateOffers = React.memo(
  ({
    privateOffersForTable,
    crmOpportunityId,
    openModal,
    selectedPrivateOffer,
    handleModalClose,
    planType,
    DetailedOfferPreviewComponent,
  }: PrivateOffersProps) => {
    const dispatch = useDispatch()

    const { pageSize, noOfPage, pageNumber } = useSelector<PrivateOffersState>(
      state => state.privateOffers
    )
    const { selectedProduct: productId } =
      useFlyOutProductsBasedOnSelectedCloud()
    const cloudMarketplace = useCurrentCloudProvider()
    //TO DO: https://ibexlabs.atlassian.net/browse/LT-3594 Remove these partner_id from params
    const { partner_id: accountIdFromParams } = useParams<{
      partner_id?: string
    }>()
    const accountId =
      useAccountId({ product: 'flyout' }) || accountIdFromParams || ''
    const isAdmin = useUserType() === PartnerType.Admin

    const noOp = useCallback(() => void 0, [])
    const onChangeRowsPerPage = useCallback(
      (e: SelectChangeEvent) => {
        dispatch(setPageNumber(1))
        const noOfRows = Number(e.target.value)
        if (accountId !== null) {
          dispatch(
            updatePageSize({
              partnerId: accountId,
              cloudMarketplace,
              pageSize: noOfRows,
              productId,
              crmOpportunityId,
            })
          )
        }
      },
      [accountId, productId, cloudMarketplace, crmOpportunityId, dispatch]
    )
    const handlePageChange = useCallback(
      (e: React.ChangeEvent<unknown>, pageNumber: number) => {
        dispatch(setPageNumber(pageNumber))
        if (accountId !== null) {
          dispatch(
            getPrivateOffers({
              partnerId: accountId,
              cloudMarketplace,
              pageSize,
              pageNumber,
              productId,
              crmOpportunityId,
            })
          )
        }
      },
      [
        accountId,
        pageSize,
        productId,
        cloudMarketplace,
        crmOpportunityId,
        dispatch,
      ]
    )

    return (
      <div data-testid="private-offers-table">
        <TableComponent
          mapping={
            isAdmin
              ? mappingOnAdminUI
              : getMappingPOBasedOnCloud(cloudMarketplace)
          }
          objects={privateOffersForTable}
          component="div"
          hidePagination={false}
          onChange={handlePageChange}
          onChangeRowsPerPage={onChangeRowsPerPage}
          onClick={noOp}
          noHover={true}
          rowsPerPageOptions={[10, 20, 30]}
          pageNumber={pageNumber}
          count={noOfPage || 0}
          rowsPerPage={pageSize}
        />
        <ModalConfirm
          title={'Preview'}
          subTitle={
            DetailedOfferPreviewComponent ? (
              <DetailedOfferPreviewComponent
                offer={selectedPrivateOffer}
                planType={planType}
              />
            ) : undefined
          }
          content={''}
          open={openModal}
          titleAcceptButton={'Okay'}
          onAccept={handleModalClose}
          onDecline={handleModalClose}
          variant={ModalSizeVariants.medium}
          hideCancelButton={true}
        />
      </div>
    )
  }
)

export default PrivateOffers
